/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const zhTW: LocalizationResource = {
  locale: 'zh-TW',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: '返回',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: '預設',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__otherImpersonatorDevice: '其他模擬裝置',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: '主要',
  badge__renewsAt: undefined,
  badge__requiresAction: '需要操作',
  badge__startsAt: undefined,
  badge__thisDevice: '此設備',
  badge__unverified: '未驗證',
  badge__upcomingPlan: undefined,
  badge__userDevice: '用戶設備',
  badge__you: '您',
  commerce: {
    addPaymentMethod: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedMonthlyOnly: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethods: undefined,
    paymentSource: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    popular: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
    },
    subtotal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  createOrganization: {
    formButtonSubmit: '創建組織',
    invitePage: {
      formButtonReset: '跳過',
    },
    title: '創建組織',
  },
  dates: {
    lastDay: "昨天{{ date | timeString('zh-TW') }}",
    next6Days: "{{ date | weekday('zh-TW','long') }} {{ date | timeString('zh-TW') }}",
    nextDay: "明天{{ date | timeString('zh-TW') }}",
    numeric: "{{ date | numeric('zh-TW') }}",
    previous6Days: "上週{{ date | weekday('zh-TW','long') }} {{ date | timeString('zh-TW') }}",
    sameDay: "今天{{ date | timeString('zh-TW') }}",
  },
  dividerText: '或者',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: '使用另一種方法',
  footerPageLink__help: '幫助',
  footerPageLink__privacy: '隱私',
  footerPageLink__terms: '條款',
  formButtonPrimary: '繼續',
  formButtonPrimary__verify: '驗證',
  formFieldAction__forgotPassword: '忘記密碼？',
  formFieldError__matchingPasswords: '密碼匹配。',
  formFieldError__notMatchingPasswords: '密碼不匹配。',
  formFieldError__verificationLinkExpired: '驗證連結已過期，請請求新的連結。',
  formFieldHintText__optional: '選填',
  formFieldHintText__slug: 'slug 是一個易於人類閱讀且必須唯一的 ID，通常用於 URL。',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: undefined,
  formFieldInputPlaceholder__confirmDeletionUserAccount: '刪除帳戶',
  formFieldInputPlaceholder__emailAddress: undefined,
  formFieldInputPlaceholder__emailAddress_username: undefined,
  formFieldInputPlaceholder__emailAddresses: '輸入或貼上一個或多個電子郵件地址，以空格或逗號分隔',
  formFieldInputPlaceholder__firstName: undefined,
  formFieldInputPlaceholder__lastName: undefined,
  formFieldInputPlaceholder__organizationDomain: undefined,
  formFieldInputPlaceholder__organizationDomainEmailAddress: undefined,
  formFieldInputPlaceholder__organizationName: undefined,
  formFieldInputPlaceholder__organizationSlug: undefined,
  formFieldInputPlaceholder__password: undefined,
  formFieldInputPlaceholder__phoneNumber: undefined,
  formFieldInputPlaceholder__username: undefined,
  formFieldLabel__apiKeyDescription: undefined,
  formFieldLabel__apiKeyExpiration: undefined,
  formFieldLabel__apiKeyName: undefined,
  formFieldLabel__automaticInvitations: '為此網域啟用自動邀請',
  formFieldLabel__backupCode: '備用碼',
  formFieldLabel__confirmDeletion: '確定',
  formFieldLabel__confirmPassword: '確認密碼',
  formFieldLabel__currentPassword: '目前密碼',
  formFieldLabel__emailAddress: '電子郵件地址',
  formFieldLabel__emailAddress_username: '電子郵件地址或使用者名稱',
  formFieldLabel__emailAddresses: '電子郵件地址',
  formFieldLabel__firstName: '名字',
  formFieldLabel__lastName: '姓氏',
  formFieldLabel__newPassword: '新密碼',
  formFieldLabel__organizationDomain: '網域',
  formFieldLabel__organizationDomainDeletePending: '刪除待處理的邀請和建議',
  formFieldLabel__organizationDomainEmailAddress: '驗證用電子郵件地址',
  formFieldLabel__organizationDomainEmailAddressDescription: '輸入此網域下的一個電子郵件地址以接收代碼並驗證此網域。',
  formFieldLabel__organizationName: '組織名稱',
  formFieldLabel__organizationSlug: 'URL 代稱',
  formFieldLabel__passkeyName: undefined,
  formFieldLabel__password: '密碼',
  formFieldLabel__phoneNumber: '電話號碼',
  formFieldLabel__role: '角色',
  formFieldLabel__signOutOfOtherSessions: '登出所有其他裝置',
  formFieldLabel__username: '使用者名稱',
  impersonationFab: {
    action__signOut: '登出',
    title: '以 {{identifier}} 身份登入',
  },
  maintenanceMode: undefined,
  membershipRole__admin: '管理員',
  membershipRole__basicMember: '成員',
  membershipRole__guestMember: '訪客',
  organizationList: {
    action__createOrganization: '建立組織',
    action__invitationAccept: '加入',
    action__suggestionsAccept: '請求加入',
    createOrganization: '建立組織',
    invitationAcceptedLabel: '已加入',
    subtitle: '以繼續前往 {{applicationName}}',
    suggestionsAcceptedLabel: '待核准',
    title: '選擇一個帳戶',
    titleWithoutPersonal: '選擇一個組織',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: '自動邀請',
    badge__automaticSuggestion: '自動建議',
    badge__manualInvitation: '未啟用自動註冊',
    badge__unverified: '未驗證',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle: '新增要驗證的網域。在此網域下擁有電子郵件地址的使用者可以自動加入組織或請求加入。',
      title: '新增網域',
    },
    invitePage: {
      detailsTitle__inviteFailed: '邀請無法傳送。請修正以下問題後再試一次：',
      formButtonPrimary__continue: '傳送邀請',
      selectDropdown__role: '選取角色',
      subtitle: '邀請新成員加入此組織',
      successMessage: '邀請已成功傳送',
      title: '邀請成員',
    },
    membersPage: {
      action__invite: '邀請',
      action__search: undefined,
      activeMembersTab: {
        menuAction__remove: '移除成員',
        tableHeader__actions: undefined,
        tableHeader__joined: '加入',
        tableHeader__role: '角色',
        tableHeader__user: '使用者',
      },
      detailsTitle__emptyRow: '沒有可顯示的成員',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            '透過將電子郵件網域與您的組織連結來邀請使用者。任何使用匹配的電子郵件網域註冊的人都可以隨時加入該組織。',
          headerTitle: '自動邀請',
          primaryButton: '管理已驗證網域',
        },
        table__emptyRow: '沒有可顯示的邀請',
      },
      invitedMembersTab: {
        menuAction__revoke: '撤銷邀請',
        tableHeader__invited: '已邀請',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle: '使用匹配的電子郵件網域註冊的使用者將能夠看到請求加入您組織的建議。',
          headerTitle: '自動建議',
          primaryButton: '管理已驗證網域',
        },
        menuAction__approve: '核准',
        menuAction__reject: '拒絕',
        tableHeader__requested: '請求存取',
        table__emptyRow: '沒有可顯示的請求',
      },
      start: {
        headerTitle__invitations: '邀請',
        headerTitle__members: '成員',
        headerTitle__requests: '請求',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: '管理您的組織設定。',
      general: '一般',
      members: '成員',
      title: '組織',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: '在下方輸入「{{organizationName}}」以繼續。',
          messageLine1: '您確定要刪除此組織嗎？',
          messageLine2: '此動作將永久生效且無法復原。',
          successMessage: '您已刪除該組織。',
          title: '刪除組織',
        },
        leaveOrganization: {
          actionDescription: '在下方輸入「{{organizationName}}」以繼續。',
          messageLine1: '您確定要離開此組織嗎？您將失去對此組織及其應用程式的存取權限。',
          messageLine2: '此動作將永久生效且無法復原。',
          successMessage: '您已離開該組織。',
          title: '離開組織',
        },
        title: '危險',
      },
      domainSection: {
        menuAction__manage: '管理',
        menuAction__remove: '刪除',
        menuAction__verify: '驗證',
        primaryButton: '新增網域',
        subtitle: '允許使用者根據已驗證的電子郵件網域自動加入組織或請求加入。',
        title: '已驗證網域',
      },
      successMessage: '組織資訊已更新。',
      title: '組織設定',
    },
    removeDomainPage: {
      messageLine1: '電子郵件網域 {{domain}} 將被移除。',
      messageLine2: '此後，使用者將無法透過此網域自動加入組織。',
      successMessage: '網域 {{domain}} 已被移除。',
      title: '移除網域',
    },
    start: {
      headerTitle__general: '一般',
      headerTitle__members: '成員',
      profileSection: {
        primaryButton: undefined,
        title: '組織資料',
        uploadAction__title: '標誌',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: '移除此網域將影響已邀請的使用者。',
        removeDomainActionLabel__remove: '移除網域',
        removeDomainSubtitle: '從您已驗證的網域中移除此網域',
        removeDomainTitle: '移除網域',
      },
      enrollmentTab: {
        automaticInvitationOption__description: '使用者註冊時會自動受邀加入組織，並可隨時加入。',
        automaticInvitationOption__label: '自動邀請',
        automaticSuggestionOption__description: '使用者會收到請求加入的建議，但必須由管理員核准後才能加入組織。',
        automaticSuggestionOption__label: '自動建議',
        calloutInfoLabel: '變更註冊模式只會影響新使用者。',
        calloutInvitationCountLabel: '已傳送給使用者的待處理邀請：{{count}}',
        calloutSuggestionCountLabel: '已傳送給使用者的待處理建議：{{count}}',
        manualInvitationOption__description: '使用者只能透過手動邀請加入組織。',
        manualInvitationOption__label: '未啟用自動註冊',
        subtitle: '選擇此網域的使用者如何加入組織。',
      },
      start: {
        headerTitle__danger: '危險',
        headerTitle__enrollment: '註冊選項',
      },
      subtitle: '網域 {{domain}} 目前已驗證。請繼續選擇註冊模式。',
      title: '更新 {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: '請輸入傳送至您電子郵件地址的驗證碼',
      formTitle: '驗證碼',
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '網域 {{domainName}} 需要透過電子郵件進行驗證。',
      subtitleVerificationCodeScreen: '驗證碼已傳送至 {{emailAddress}}。請輸入代碼以繼續。',
      title: '驗證網域',
    },
  },
  organizationSwitcher: {
    action__createOrganization: '建立組織',
    action__invitationAccept: '加入',
    action__manageOrganization: '管理組織',
    action__suggestionsAccept: '請求加入',
    notSelected: '未選取組織',
    personalWorkspace: '個人工作區',
    suggestionsAcceptedLabel: '待核准',
  },
  paginationButton__next: '下一頁',
  paginationButton__previous: '上一頁',
  paginationRowText__displaying: '顯示',
  paginationRowText__of: '的',
  reverification: {
    alternativeMethods: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__backupCode: undefined,
      blockButton__emailCode: undefined,
      blockButton__passkey: undefined,
      blockButton__password: undefined,
      blockButton__phoneCode: undefined,
      blockButton__totp: undefined,
      getHelp: {
        blockButton__emailSupport: undefined,
        content: undefined,
        title: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: undefined,
      title: undefined,
    },
    emailCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    noAvailableMethods: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCodeMfa: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    totpMfa: {
      formTitle: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: '新增帳戶',
      action__signOutAll: '登出所有帳戶',
      subtitle: '選擇您想要繼續使用的帳戶。',
      title: '選擇一個帳戶',
    },
    alternativeMethods: {
      actionLink: '取得協助',
      actionText: '沒有以上任何一種方式嗎？',
      blockButton__backupCode: '使用備用碼',
      blockButton__emailCode: '傳送驗證碼至 {{identifier}}',
      blockButton__emailLink: '傳送電子郵件連結至 {{identifier}}',
      blockButton__passkey: undefined,
      blockButton__password: '使用您的密碼登入',
      blockButton__phoneCode: '傳送簡訊代碼至 {{identifier}}',
      blockButton__totp: '使用您的驗證器應用程式',
      getHelp: {
        blockButton__emailSupport: '電子郵件支援',
        content: '如果您登入帳戶時遇到問題，請傳送電子郵件給我們，我們將盡快協助您恢復存取權限。',
        title: '取得協助',
      },
      subtitle: '遇到問題？您可以使用下列任一方法登入。',
      title: '使用其他方式',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: '以繼續前往 {{applicationName}}',
      title: '輸入備用碼',
    },
    emailCode: {
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查您的電子郵件',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      expired: {
        subtitle: '返回原先的分頁以繼續。',
        title: '此驗證連結已過期',
      },
      failed: {
        subtitle: '返回原先的分頁以繼續。',
        title: '此驗證連結無效',
      },
      formSubtitle: '使用傳送至您電子郵件的驗證連結',
      formTitle: '驗證連結',
      loading: {
        subtitle: '即將為您重新導向',
        title: '正在登入…',
      },
      resendButton: '重新傳送連結',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查您的電子郵件',
      unusedTab: {
        title: '您可以關閉此分頁',
      },
      verified: {
        subtitle: '即將為您重新導向',
        title: '成功登入',
      },
      verifiedSwitchTab: {
        subtitle: '返回原先的分頁以繼續',
        subtitleNewTab: '返回新開啟的分頁以繼續',
        titleNewTab: '已在其他分頁登入',
      },
    },
    forgotPassword: {
      formTitle: '重設密碼代碼',
      resendButton: '重新傳送代碼',
      subtitle: '以重設您的密碼',
      subtitle_email: '首先，請輸入傳送至您電子郵件的代碼',
      subtitle_phone: '首先，請輸入傳送至您手機的代碼',
      title: '重設密碼',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: '重設密碼',
      label__alternativeMethods: '或者，使用其他方式登入。',
      title: '忘記密碼？',
    },
    noAvailableMethods: {
      message: '無法繼續登入。沒有可用的驗證方式。',
      subtitle: '發生錯誤',
      title: '無法登入',
    },
    passkey: {
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: '使用其他方式',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '輸入您的密碼',
    },
    passwordPwned: {
      title: undefined,
    },
    phoneCode: {
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查手機簡訊',
    },
    phoneCodeMfa: {
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: undefined,
      title: '檢查手機簡訊',
    },
    resetPassword: {
      formButtonPrimary: '重設密碼',
      requiredMessage: '基於安全考量，您必須重設密碼。',
      successMessage: '您的密碼已成功變更。正在為您登入，請稍候。',
      title: '重設密碼',
    },
    resetPasswordMfa: {
      detailsLabel: '我們需要驗證您的身份才能重設您的密碼。',
    },
    start: {
      actionLink: '註冊',
      actionLink__join_waitlist: undefined,
      actionLink__use_email: '使用電子郵件',
      actionLink__use_email_username: '使用電子郵件或使用者名稱',
      actionLink__use_passkey: undefined,
      actionLink__use_phone: '使用電話號碼',
      actionLink__use_username: '使用使用者名稱',
      actionText: '還沒有帳戶嗎？',
      actionText__join_waitlist: undefined,
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '以繼續前往 {{applicationName}}',
      subtitleCombined: undefined,
      title: '登入',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: '驗證碼',
      subtitle: undefined,
      title: '兩步驟驗證',
    },
  },
  signInEnterPasswordTitle: '輸入您的密碼',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: '登入',
      actionText: '已經有帳戶了嗎？',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '填寫缺少的欄位',
    },
    emailCode: {
      formSubtitle: '請輸入傳送至您電子郵件地址的驗證碼',
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '驗證您的電子郵件',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      formSubtitle: '使用傳送至您電子郵件地址的驗證連結',
      formTitle: '驗證連結',
      loading: {
        title: '正在註冊…',
      },
      resendButton: '重新傳送連結',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '驗證您的電子郵件',
      verified: {
        title: '註冊成功',
      },
      verifiedSwitchTab: {
        subtitle: '返回新開啟的分頁以繼續',
        subtitleNewTab: '返回上一個分頁以繼續',
        title: '電子郵件驗證成功',
      },
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: '我同意 {{ privacyPolicyLink || link("隱私權政策") }}',
        label__onlyTermsOfService: '我同意 {{ termsOfServiceLink || link("服務條款") }}',
        label__termsOfServiceAndPrivacyPolicy:
          '我同意 {{ termsOfServiceLink || link("服務條款") }} 與 {{ privacyPolicyLink || link("隱私權政策") }}',
      },
      continue: {
        subtitle: '請閱讀並接受條款以繼續',
        title: '同意條款',
      },
    },
    phoneCode: {
      formSubtitle: '輸入傳送到您的電話號碼的驗證碼',
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '驗證您的電話號碼',
    },
    restrictedAccess: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__emailSupport: undefined,
      blockButton__joinWaitlist: undefined,
      subtitle: undefined,
      subtitleWaitlist: undefined,
      title: undefined,
    },
    start: {
      actionLink: '登入',
      actionLink__use_email: undefined,
      actionLink__use_phone: undefined,
      actionText: '已經有帳戶了嗎？',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '以繼續前往 {{applicationName}}',
      subtitleCombined: '以繼續前往 {{applicationName}}',
      title: '建立您的帳戶',
      titleCombined: '建立您的帳戶',
    },
  },
  socialButtonsBlockButton: '以 {{provider|titleize}} 帳戶登入',
  socialButtonsBlockButtonManyInView: undefined,
  unstable__errors: {
    already_a_member_in_organization: undefined,
    captcha_invalid: '由於安全驗證失敗，註冊未成功。請重新整理頁面再試一次，或聯絡支援以取得協助。',
    captcha_unavailable: '由於機器人驗證失敗導致註冊未成功。請重新整理頁面再試一次，或聯絡支援以取得協助。',
    form_code_incorrect: undefined,
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: '我們無法找到具有這些信息的帳戶。',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: '電子郵件地址必須是有效的格式。',
    form_param_format_invalid__phone_number: '電話號碼必須是有效的國際格式。',
    form_param_max_length_exceeded__first_name: '名字不應超過 256 個字元。',
    form_param_max_length_exceeded__last_name: '姓氏不應超過 256 個字元。',
    form_param_max_length_exceeded__name: '名稱不應超過 256 個字元。',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: undefined,
    form_password_not_strong_enough: '您的密碼強度不足。',
    form_password_pwned: '此密碼已在已知的資料外洩事件中出現，請改用其他密碼。',
    form_password_pwned__sign_in: undefined,
    form_password_size_in_bytes_exceeded: '您的密碼超過允許的大小上限，請縮短或移除部分特殊字元。',
    form_password_validation_failed: '密碼驗證失敗',
    form_username_invalid_character: undefined,
    form_username_invalid_length: undefined,
    identification_deletion_failed: '您無法刪除最後一個驗證方式。',
    not_allowed_access: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    passkey_already_exists: undefined,
    passkey_not_supported: undefined,
    passkey_pa_not_supported: undefined,
    passkey_registration_cancelled: undefined,
    passkey_retrieval_cancelled: undefined,
    passwordComplexity: {
      maximumLength: '少於 {{length}} 個字元',
      minimumLength: '{{length}} 個或更多字元',
      requireLowercase: '一個小寫字母',
      requireNumbers: '一個數字',
      requireSpecialCharacter: '一個特殊字元',
      requireUppercase: '一個大寫字母',
      sentencePrefix: '您的密碼必須包含：',
    },
    phone_number_exists: '此電話號碼已被使用，請嘗試其他號碼。',
    session_exists: '您已經登錄。',
    web3_missing_identifier: undefined,
    zxcvbn: {
      couldBeStronger: '您的密碼強度尚可，但可以更安全。請嘗試增加長度或複雜度。',
      goodPassword: '密碼強度良好。',
      notEnough: '您的密碼強度不足。',
      suggestions: {
        allUppercase: '部分字母大寫，但非全部。',
        anotherWord: '增加更多、更少見的單字。',
        associatedYears: '避免使用與您相關的年份。',
        capitalization: '不僅首字母大寫。',
        dates: '避免使用與您相關的日期和年份。',
        l33t: '避免可預測的字母替換（例如用 "@" 取代 "a"）。',
        longerKeyboardPattern: '使用更長的鍵盤排列，並多次改變方向。',
        noNeed: '您無需使用符號、數字或大寫字母也能建立高強度密碼。',
        pwned: '若您在其他地方使用過此密碼，建議立即更換。',
        recentYears: '避免使用近年的年份。',
        repeated: undefined,
        reverseWords: '避免常用詞語的反向拼寫。',
        sequences: '避免常見的字元序列。',
        useWords: '使用多個單字，但避免常用片語。',
      },
      warnings: {
        common: '這是一個常用的密碼。',
        commonNames: '常見的姓名容易被猜到。',
        dates: '日期易被猜到。',
        extendedRepeat: '重複的字元模式（例如 "abcabcabc"）容易被猜到。',
        keyPattern: '簡短的鍵盤排列容易被猜到。',
        namesByThemselves: '單獨使用姓名容易被猜到。',
        pwned: '您的密碼已在公開的資料外洩事件中出現。',
        recentYears: '近年的年份容易被猜到。',
        sequences: '常見的字元序列（例如 "abc"）容易被猜到。',
        similarToCommon: '此密碼與常見密碼相似。',
        simpleRepeat: '重複的字元（例如 "aaa"）容易被猜到。',
        straightRow: '鍵盤上成直線的按鍵容易被猜到。',
        topHundred: '這是非常常用的密碼。',
        topTen: '這是極常用的密碼。',
        userInputs: '密碼不應包含個人或頁面相關資訊。',
        wordByItself: '單獨使用單字容易被猜到。',
      },
    },
  },
  userButton: {
    action__addAccount: '新增帳戶',
    action__manageAccount: '管理帳戶',
    action__signOut: '登出',
    action__signOutAll: '登出所有帳戶',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: '已複製！',
      actionLabel__copy: '全部複製',
      actionLabel__download: '下載 .txt',
      actionLabel__print: '列印',
      infoText1: '將為此帳戶啟用備用碼。',
      infoText2: '請妥善保管您的備用碼。若懷疑已外洩，您可以重新產生。',
      subtitle__codelist: '請妥善保管。',
      successMessage: '備用碼現已啟用。若您無法存取驗證裝置，可使用其中一組備用碼登入。每組代碼僅能使用一次。',
      successSubtitle: '若您無法存取驗證裝置，可使用其中一組備用碼登入您的帳戶。',
      title: '新增備用碼驗證',
      title__codelist: '備用碼',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: '選擇一個供應商以連結您的帳戶。',
      formHint__noAccounts: '目前無可用的外部帳戶供應商。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此已連結帳戶，任何依賴此連結的功能也將失效。',
        successMessage: '{{connectedAccount}} 已從您的帳戶中移除。',
        title: '移除已連結的帳戶',
      },
      socialButtonsBlockButton: '連結 {{provider|titleize}} 帳戶',
      successMessage: '供應商已新增至您的帳戶',
      title: '新增已連結的帳戶',
    },
    deletePage: {
      actionDescription: '請在下方輸入「Delete account」以繼續。',
      confirm: '刪除帳戶',
      messageLine1: '您確定要刪除您的帳戶嗎？',
      messageLine2: '此動作將永久生效且無法復原。',
      title: '刪除帳戶',
    },
    emailAddressPage: {
      emailCode: {
        formHint: '系統將會傳送一封包含驗證碼的郵件至此電子郵件地址。',
        formSubtitle: '請輸入傳送至 {{identifier}} 的驗證碼',
        formTitle: '驗證碼',
        resendButton: '重新傳送驗證碼',
        successMessage: '電子郵件 {{identifier}} 已新增至您的帳戶。',
      },
      emailLink: {
        formHint: '系統將會傳送一封包含驗證連結的郵件至此電子郵件地址。',
        formSubtitle: '請點擊傳送至 {{identifier}} 的郵件中的驗證連結',
        formTitle: '驗證連結',
        resendButton: '重新傳送連結',
        successMessage: '電子郵件 {{identifier}} 已新增至您的帳戶。',
      },
      enterpriseSSOLink: {
        formButton: undefined,
        formSubtitle: undefined,
      },
      formHint: undefined,
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此電子郵件地址登入。',
        successMessage: '電子郵件 {{emailAddress}} 已從您的帳戶中移除。',
        title: '移除電子郵件地址',
      },
      title: '新增電子郵件地址',
      verifyTitle: '驗證電子郵件地址',
    },
    formButtonPrimary__add: '新增',
    formButtonPrimary__continue: '繼續',
    formButtonPrimary__finish: '完成',
    formButtonPrimary__remove: '移除',
    formButtonPrimary__save: '儲存',
    formButtonReset: '取消',
    mfaPage: {
      formHint: '選擇一種新增方式。',
      title: '新增兩步驟驗證',
    },
    mfaPhoneCodePage: {
      backButton: '使用現有號碼',
      primaryButton__addPhoneNumber: '新增電話號碼',
      removeResource: {
        messageLine1: '{{identifier}} 將不再用於接收登入驗證碼。',
        messageLine2: '您的帳戶安全性可能會降低，確定要繼續嗎？',
        successMessage: '已移除 {{mfaPhoneCode}} 的簡訊兩步驟驗證',
        title: '移除兩步驟驗證',
      },
      subtitle__availablePhoneNumbers: '選擇一個電話號碼以註冊簡訊兩步驟驗證。',
      subtitle__unavailablePhoneNumbers: '目前沒有可用的電話號碼以註冊簡訊兩步驟驗證。',
      successMessage1: '登入時，您需要輸入傳送到此電話號碼的驗證碼，作為額外的驗證步驟。',
      successMessage2: '請妥善儲存這些備用碼。若您無法存取驗證裝置，可以使用備用碼登入。',
      successTitle: '簡訊驗證已啟用',
      title: '新增簡訊驗證',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: '掃描 QR Code',
        buttonUnableToScan__nonPrimary: '無法掃描 QR Code？',
        infoText__ableToScan: '在您的驗證器應用程式中設定新的登入方式，並掃描下方的 QR Code 以連結您的帳戶。',
        infoText__unableToScan: '在驗證器中設定新的登入方式，並輸入下方提供的金鑰 (Key)。',
        inputLabel__unableToScan1: '請確認已啟用 TOTP (基於時間的一次性密碼)，然後完成帳戶連結。',
        inputLabel__unableToScan2: '或者，若您的驗證器支援 TOTP URI，您也可以複製完整的 URI。',
      },
      removeResource: {
        messageLine1: '登入時將不再需要輸入此驗證器的驗證碼。',
        messageLine2: '您的帳戶安全性可能會降低，確定要繼續嗎？',
        successMessage: '已移除透過驗證器應用程式的兩步驟驗證。',
        title: '移除兩步驟驗證',
      },
      successMessage: '兩步驟驗證現已啟用。登入時，您需要輸入此驗證器產生的驗證碼作為額外步驟。',
      title: '新增驗證器應用程式',
      verifySubtitle: '請輸入您的驗證器產生的驗證碼',
      verifyTitle: '驗證碼',
    },
    mobileButton__menu: '選單',
    navbar: {
      account: '個人資料',
      apiKeys: undefined,
      billing: undefined,
      description: '管理您的帳戶資訊。',
      security: '安全性',
      title: '帳戶',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: undefined,
        title: undefined,
      },
      subtitle__rename: undefined,
      title__rename: undefined,
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions: '建議您登出所有可能使用過舊密碼的其他裝置。',
      readonly: '您目前無法編輯密碼，因為您只能透過企業連線登入。',
      successMessage__set: '您的密碼已設定。',
      successMessage__signOutOfOtherSessions: '已登出所有其他裝置。',
      successMessage__update: '您的密碼已更新。',
      title__set: '設定密碼',
      title__update: '變更密碼',
    },
    phoneNumberPage: {
      infoText: '系統將會傳送一封包含驗證連結的簡訊至此電話號碼。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此電話號碼登入。',
        successMessage: '電話號碼 {{phoneNumber}} 已從您的帳戶中移除。',
        title: '移除電話號碼',
      },
      successMessage: '{{identifier}} 已新增至您的帳戶。',
      title: '新增電話號碼',
      verifySubtitle: '請輸入傳送至 {{identifier}} 的驗證碼',
      verifyTitle: '驗證電話號碼',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: '上傳 JPG, PNG, GIF, 或 WEBP 格式圖片（檔案大小上限 10MB）',
      imageFormDestructiveActionSubtitle: '移除圖片',
      imageFormSubtitle: '上傳圖片',
      imageFormTitle: '個人資料圖片',
      readonly: '您的個人資料由企業連線提供，無法編輯。',
      successMessage: '您的個人資料已更新。',
      title: '更新個人資料',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: '登出裝置',
        title: '已登入的裝置',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: '再試一次',
        actionLabel__reauthorize: '立即重新授權',
        destructiveActionTitle: '移除',
        primaryButton: '連結帳戶',
        subtitle__disconnected: undefined,
        subtitle__reauthorize: '必要的權限範圍已更新，您可能會遇到功能受限的情況。請重新授權此應用程式以避免任何問題。',
        title: '已連結的帳戶',
      },
      dangerSection: {
        deleteAccountButton: '刪除帳戶',
        title: '危險',
      },
      emailAddressesSection: {
        destructiveAction: '移除電子郵件地址',
        detailsAction__nonPrimary: '設為主要',
        detailsAction__primary: '完成驗證',
        detailsAction__unverified: '完成驗證',
        primaryButton: '新增電子郵件地址',
        title: '電子郵件地址',
      },
      enterpriseAccountsSection: {
        title: '企業帳戶',
      },
      headerTitle__account: '帳戶',
      headerTitle__security: '安全性',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: '重新產生代碼',
          headerTitle: '備用碼',
          subtitle__regenerate: '取得一組新的安全備用碼。先前產生的備用碼將失效。',
          title__regenerate: '重新產生備用碼',
        },
        phoneCode: {
          actionLabel__setDefault: '設為預設',
          destructiveActionLabel: '移除電話號碼',
        },
        primaryButton: '新增兩步驟驗證',
        title: '兩步驟驗證',
        totp: {
          destructiveActionTitle: '移除',
          headerTitle: '驗證器應用程式',
        },
      },
      passkeysSection: {
        menuAction__destructive: undefined,
        menuAction__rename: undefined,
        primaryButton: undefined,
        title: undefined,
      },
      passwordSection: {
        primaryButton__setPassword: '設定密碼',
        primaryButton__updatePassword: '變更密碼',
        title: '密碼',
      },
      phoneNumbersSection: {
        destructiveAction: '移除電話號碼',
        detailsAction__nonPrimary: '設為主要',
        detailsAction__primary: '完成驗證',
        detailsAction__unverified: '完成驗證',
        primaryButton: '新增電話號碼',
        title: '電話號碼',
      },
      profileSection: {
        primaryButton: '更新個人資料',
        title: '個人資料',
      },
      usernameSection: {
        primaryButton__setUsername: '設定使用者名稱',
        primaryButton__updateUsername: '變更使用者名稱',
        title: '使用者名稱',
      },
      web3WalletsSection: {
        destructiveAction: '移除錢包',
        detailsAction__nonPrimary: undefined,
        primaryButton: '新增 Web3 錢包',
        title: 'Web3 錢包',
      },
    },
    usernamePage: {
      successMessage: '您的使用者名稱已更新。',
      title__set: '設定使用者名稱',
      title__update: '變更使用者名稱',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此 web3 錢包登入。',
        successMessage: '{{web3Wallet}} 已從您的帳戶中移除。',
        title: '移除 Web3 錢包',
      },
      subtitle__availableWallets: '選擇一個 web3 錢包以連結您的帳戶。',
      subtitle__unavailableWallets: '目前沒有可用的 web3 錢包。',
      successMessage: '錢包已新增至您的帳戶。',
      title: '新增 web3 錢包',
      web3WalletButtonsBlockButton: undefined,
    },
  },
  waitlist: {
    start: {
      actionLink: undefined,
      actionText: undefined,
      formButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    success: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
} as const;
